import CheckBoxComponent from "@/components/shared/checkBox";
import Drawer from "@/components/shared/drawer";
import { autoCompeleteSelectFlag } from "@/states/owner/task/autoCompeleteSelectAtom";
import { ApplyFilter } from "@/types/owner/task/applyFilter";
import { useAtom } from "jotai";
import { Dispatch, SetStateAction, useCallback } from "react";

type ShowFilterList = {
  targetMonth: boolean;
  status: boolean;
  workFrequency: boolean;
  crew: boolean;
  jobCategory: boolean;
  problem: boolean;
};

type OwnerFilterList = {
  scheduledMonth: string[];
  statusId?: number[];
  taskreqstatus_id?: number[];
  frequency: string;
  crew_ids: number[];
  taskcategory_id: number[];
  problem: boolean;
  suggestion?: string;
};

type Props = {
  showFilterSelectDrawer: boolean;
  setShowFilterSelectDrawer: Dispatch<SetStateAction<boolean>>;
  showFilterList: ShowFilterList;
  setShowFilterList: Dispatch<SetStateAction<ShowFilterList>>;
  ownerFilterList: OwnerFilterList;
  setOwnerFilterList: Dispatch<SetStateAction<OwnerFilterList>>;
  setApplyFilter: Dispatch<SetStateAction<ApplyFilter>>;
  tab?: string;
};

const OwnerTaskFilterDrawer = ({
  showFilterSelectDrawer,
  setShowFilterSelectDrawer,
  showFilterList,
  setShowFilterList,
  setOwnerFilterList,
  setApplyFilter,
  tab
}: Props) => {
  // 自動完了フィルターのフラグ
  const [autoCompeleteSelect, setAutoCompeleteSelect] = useAtom(
    autoCompeleteSelectFlag
  );

  // チェックボックスのクリックハンドラーをメモ化
  const handleAllCheckboxClick = useCallback(() => {
    const allChecked =
      showFilterList.targetMonth &&
      showFilterList.status &&
      showFilterList.workFrequency &&
      showFilterList.crew &&
      showFilterList.jobCategory &&
      showFilterList.problem;

    // 状態更新を一括で行い、不要な再レンダリングを防ぐ
    requestAnimationFrame(() => {
      setShowFilterList((prev) => ({
        ...prev,
        targetMonth: !allChecked,
        status: !allChecked,
        workFrequency: !allChecked,
        crew: !allChecked,
        jobCategory: !allChecked,
        problem: !allChecked
      }));

      if (allChecked) {
        setOwnerFilterList((prev: OwnerFilterList) => ({
          ...prev,
          scheduledMonth: [],
          ...(tab !== "contract" && {
            statusId: tab === "waiting" ? [1] : tab === "task" ? [3, 4, 5] : []
          }),
          ...(tab === "contract" && { taskreqstatus_id: [] }),
          frequency: "",
          crew_ids: [],
          taskcategory_id: [],
          problem: false,
          ...(tab === "suggestion" && { suggestion: "" })
        }));
        setApplyFilter("filter");
      }
    });
  }, [
    showFilterList,
    setShowFilterList,
    setOwnerFilterList,
    setApplyFilter,
    tab
  ]);

  // 個別のチェックボックスハンドラーを生成する関数
  const createCheckboxHandler = useCallback(
    (key: keyof ShowFilterList, filterUpdate: () => void) => {
      let isProcessing = false; // デバウンス用フラグ

      return () => {
        if (isProcessing) return; // 処理中なら早期リターン
        isProcessing = true;

        requestAnimationFrame(() => {
          setShowFilterList((prev) => ({
            ...prev,
            [key]: !prev[key]
          }));

          if (showFilterList[key]) {
            filterUpdate();
            setApplyFilter("filter");
          }

          // 300ms後にフラグをリセット
          setTimeout(() => {
            isProcessing = false;
          }, 300);
        });
      };
    },
    [showFilterList, setShowFilterList, setApplyFilter]
  );

  return (
    <Drawer
      title="絞り込む"
      isDrawerOpen={showFilterSelectDrawer}
      setIsDrawerOpen={setShowFilterSelectDrawer}
      height="h-[60svh]"
    >
      <div className="mx-16 mt-24 max-w-[55rem]">
        <div className="flex flex-col gap-12">
          <CheckBoxComponent
            id={"filterAllCheckbox"}
            labelText={"全て選択"}
            checked={
              tab === "suggestion"
                ? showFilterList.targetMonth &&
                  showFilterList.status &&
                  showFilterList.crew
                : tab === "waiting"
                  ? showFilterList.targetMonth &&
                    showFilterList.status &&
                    showFilterList.workFrequency &&
                    showFilterList.crew &&
                    showFilterList.jobCategory
                  : showFilterList.targetMonth &&
                    showFilterList.status &&
                    showFilterList.workFrequency &&
                    showFilterList.crew &&
                    showFilterList.jobCategory &&
                    showFilterList.problem
            }
            onClick={handleAllCheckboxClick}
          />
          <div className="ml-8 flex flex-col gap-12">
            {tab !== "contract" && (
              <CheckBoxComponent
                id={"filterTargetMonth"}
                labelText={"対象月"}
                checked={showFilterList.targetMonth}
                onClick={createCheckboxHandler("targetMonth", () => {
                  // 対象月のフィルターをクリア
                  setOwnerFilterList((prev: OwnerFilterList) => ({
                    ...prev,
                    scheduledMonth: []
                  }));
                })}
              />
            )}
            <CheckBoxComponent
              id={"filterStatus"}
              labelText={"ステータス"}
              checked={showFilterList.status}
              onClick={createCheckboxHandler("status", () => {
                // ステータスのフィルターをクリア
                setOwnerFilterList((prev: OwnerFilterList) => ({
                  ...prev,
                  ...(tab !== "contract" && {
                    statusId:
                      tab === "waiting" ? [1] : tab === "task" ? [3, 4, 5] : []
                  }),
                  ...(tab === "task" && {
                    rejected: -1,
                    is_pending: 0,
                    is_auto_completed: 0
                  }),
                  ...(tab === "waiting" && {
                    pastDue: false,
                    rejected: -1,
                    is_pending: 0,
                    rejectedOnly: 0
                  }),
                  ...(tab === "suggestion" && {
                    suggestion: "" // 提案のフィルターをクリア
                  }),
                  ...(tab === "contract" && { taskreqstatus_id: [] })
                }));
                if (autoCompeleteSelect && tab === "task") {
                  setAutoCompeleteSelect(false);
                }
              })}
            />
            {tab !== "suggestion" && (
              <CheckBoxComponent
                id={"filterWorkFrequency"}
                labelText={"作業頻度"}
                checked={showFilterList.workFrequency}
                onClick={createCheckboxHandler("workFrequency", () => {
                  // 作業頻度のフィルターをクリア
                  setOwnerFilterList((prev: OwnerFilterList) => ({
                    ...prev,
                    frequency: ""
                  }));
                })}
              />
            )}
            <CheckBoxComponent
              id={"filterCrew"}
              labelText={"クルー"}
              checked={showFilterList.crew}
              onClick={createCheckboxHandler("crew", () => {
                // クルーのフィルターをクリア
                setOwnerFilterList((prev: OwnerFilterList) => ({
                  ...prev,
                  crew_ids: []
                }));
              })}
            />
            {tab !== "suggestion" && (
              <CheckBoxComponent
                id={"filterJobCategory"}
                labelText={"仕事カテゴリ"}
                checked={showFilterList.jobCategory}
                onClick={createCheckboxHandler("jobCategory", () => {
                  // 仕事カテゴリのフィルターをクリア
                  setOwnerFilterList((prev: OwnerFilterList) => ({
                    ...prev,
                    taskcategory_id: []
                  }));
                })}
              />
            )}
            {tab === "task" && (
              <CheckBoxComponent
                id={"filterProblem"}
                labelText={"異常箇所"}
                checked={showFilterList.problem}
                onClick={createCheckboxHandler("problem", () => {
                  // 異常箇所のフィルターをクリア
                  setOwnerFilterList((prev: OwnerFilterList) => ({
                    ...prev,
                    problem: false
                  }));
                })}
              />
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default OwnerTaskFilterDrawer;
